import { AlertTriangle } from 'lucide-react';
import { Card, CardBody } from '../../components/ui/Card';
import { Field, Input, Textarea, Select, Toggle } from '../../components/ui/Field';
import { TagInput } from '../../components/ui/TagInput';
import { CostControlEditor } from '../../components/CostControlEditor';
import { cn } from '../../lib/utils';
import { TOOL_OPTIONS } from '../../lib/catalog';
import { defaultCostControl } from '../../lib/factories';
import { useModelsStore } from '../../store/modelsStore';
import type { Agent, Guardrails, ToolId } from '../../types';

export function GuardrailsTab({ agent, patch }: { agent: Agent; patch: (p: Partial<Agent>) => void }) {
  const g = agent.guardrails;
  const chatModels = useModelsStore((s) => s.catalog);
  const setG = (partial: Partial<Guardrails>) => patch({ guardrails: { ...g, ...partial } });
  const setFilters = (partial: Partial<Guardrails['contentFilters']>) =>
    setG({ contentFilters: { ...g.contentFilters, ...partial } });
  const setLimits = (partial: Partial<Guardrails['limits']>) =>
    setG({ limits: { ...g.limits, ...partial } });

  function toggleAllowedTool(tool: ToolId) {
    const has = g.allowedTools.includes(tool);
    setG({ allowedTools: has ? g.allowedTools.filter((t) => t !== tool) : [...g.allowedTools, tool] });
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardBody>
          <h2 className="mb-1 text-sm font-semibold text-ink">Custo & Budget</h2>
          <p className="mb-4 text-xs text-slate">Vincule este agente a uma área e responsável e defina o teto mensal.</p>
          <CostControlEditor
            value={agent.costControl ?? defaultCostControl()}
            onChange={(cc) => patch({ costControl: cc })}
            entityId={agent.id}
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Filtros de conteúdo</h2>
          <div className="space-y-1">
            <Toggle checked={g.contentFilters.pii} onChange={(v) => setFilters({ pii: v })} label="Detecção de PII" description="Identifica dados pessoais na entrada/saída." />
            <Toggle checked={g.contentFilters.toxicity} onChange={(v) => setFilters({ toxicity: v })} label="Toxicidade" description="Bloqueia linguagem ofensiva ou abusiva." />
            <Toggle checked={g.contentFilters.jailbreak} onChange={(v) => setFilters({ jailbreak: v })} label="Tentativa de jailbreak" description="Detecta tentativas de burlar instruções." />
          </div>
          <Field label="Tópicos proibidos" hint="Enter para adicionar" className="mt-4 mb-4">
            <TagInput values={g.contentFilters.blockedTopics} onChange={(v) => setFilters({ blockedTopics: v })} placeholder="ex.: concorrentes, política…" />
          </Field>

          <div className="grid grid-cols-1 gap-4 border-t border-line pt-4 sm:grid-cols-2">
            <Field label="Ao violar" hint="o que fazer ao detectar" className="mb-0">
              <Select value={g.onViolation} onChange={(e) => setG({ onViolation: e.target.value as Guardrails['onViolation'] })}>
                <option value="block">Bloquear</option>
                <option value="require_human">Pedir aprovação humana</option>
                <option value="warn">Avisar (não bloqueia)</option>
              </Select>
            </Field>
            <Field label="Detecção" hint="toxicidade / jailbreak / tópicos" className="mb-0">
              <Select
                value={g.moderationMode ?? 'heuristic'}
                onChange={(e) => setG({ moderationMode: e.target.value as Guardrails['moderationMode'] })}
              >
                <option value="heuristic">Heurística (rápida, sem custo)</option>
                <option value="model">Por modelo (mais precisa, gera custo)</option>
              </Select>
            </Field>
            {g.moderationMode === 'model' && (
              <Field label="Modelo de moderação" hint="classifica antes de responder" className="mb-0">
                <Select
                  value={g.moderationModel ?? 'gpt-4.1-mini'}
                  onChange={(e) => setG({ moderationModel: e.target.value })}
                >
                  {chatModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </Select>
              </Field>
            )}
          </div>
          {g.onViolation === 'warn' && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              Em "Avisar" os filtros NÃO bloqueiam — apenas deixam passar. Use "Bloquear" ou "Pedir aprovação" para interromper.
            </p>
          )}
          {g.moderationMode === 'model' && (
            <p className="mt-2 text-xs text-slate">
              Cada mensagem passa por uma checagem extra no modelo selecionado (PII continua via redação local). Mais preciso, porém adiciona uma chamada e custo por mensagem.
            </p>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Redação de PII</h2>
          <Toggle
            checked={g.redactPII}
            onChange={(v) => setG({ redactPII: v })}
            label="Mascarar PII na saída"
            description="Oculta CPF, e-mail, telefone e cartão antes de entregar a resposta."
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-1 text-sm font-semibold text-ink">Ferramentas permitidas</h2>
          <p className="mb-4 text-xs text-slate">Allowlist explícita do que o agente pode invocar.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {TOOL_OPTIONS.map((tool) => {
              const active = g.allowedTools.includes(tool.value);
              return (
                <button
                  key={tool.value}
                  onClick={() => toggleAllowedTool(tool.value)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition-colors',
                    active ? 'border-success bg-success/5 text-success' : 'border-line text-slate hover:bg-cloud',
                  )}
                >
                  <tool.icon className="h-3.5 w-3.5" />
                  {tool.label}
                </button>
              );
            })}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Limites operacionais</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Timeout (s)" hint="tempo máx. de espera pela resposta (0 = sem limite)" className="mb-0">
              <Input type="number" value={g.limits.timeoutSec} onChange={(e) => setLimits({ timeoutSec: Number(e.target.value) })} />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-1 text-sm font-semibold text-ink">Mensagem de bloqueio</h2>
          <p className="mb-4 text-xs text-slate">Mostrada quando um filtro bloqueia (a política "Ao violar" fica nos Filtros de conteúdo).</p>
          <Field label="Mensagem de fallback" className="mb-0">
            <Textarea rows={2} value={g.fallbackMessage ?? ''} onChange={(e) => setG({ fallbackMessage: e.target.value || undefined })} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-1 text-sm font-semibold text-ink">Aprovação humana obrigatória</h2>
          <p className="mb-4 text-xs text-slate">Ações sensíveis que exigem revisão humana antes de executar.</p>
          <TagInput
            values={g.humanApprovalActions}
            onChange={(v) => setG({ humanApprovalActions: v })}
            placeholder="ex.: enviar e-mail, gastar acima de US$ 500…"
          />
        </CardBody>
      </Card>
    </div>
  );
}
